import { useTranslation } from "react-i18next";
import {
  DownloadOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  ExportOutlined,
  HistoryOutlined,
  ImportOutlined,
  LoadingOutlined,
  SearchOutlined
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { Button, Dropdown, Form, message, Modal, Space, Spin } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import FormInput from "../../../../../components/form-input/FormInput";
import SafeLink from "../../../../../components/link/SafeLink";
import PageHeader from "../../../../../components/page-header/PageHeader";
import { routesConfig } from "../../../../../config/routes";
import {
  getCourseName,
} from "../../../../../service/courses";
import {
  downloadScoreList,
  downloadScoreTemplate,
  getStudentByEnrollClass,
  importScoreTemplate,
  sendScoreResult,
  updateStudentScore,
} from "../../../../../service/test-schedule";
import { EvaluationStudent } from "../../../../../types/course-management";
import { objectToQueryString } from "../../../../../utils/getPageFromLink";
import EditableTable from "./EditableTable";
import "./EvaluationStudents.scss";
import HistoryEditScoreModal from "./modal/HistoryEditScoreModal";
import ImportCommonModal from "../../../../../components/modal/import-common-modal/ImportCommonModal";

const EvaluationStudents: React.FC = () => {
  const { t } = useTranslation();
  const imgPath = "/images/";
  const { courseId, blockId, classId } = useParams();
  const [courseInfo, setCourseInfo] = useState<any>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [isEditingAll, setIsEditingAll] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(10);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [evaluationStudent, setEvaluationStudent] = useState<EvaluationStudent[]>([]);
  const [unitName, setUnitName] = useState<string>("")
  const [enrollClassName, setEnrollClassName] = useState<string>("")
  const [reload, setReload] = useState<boolean>(false);
  const [isExport, setIsExport] = useState<boolean>(false);
  const [isViewEditHistory, setIsViewEditHistory] = useState<boolean>(false);
  const [isLoadingDownloadScoreList, setIsLoadingDownloadScoreList] = useState<boolean>(false)
  const [form] = Form.useForm();
  const [timeEndTest, setTimeEndTest] = useState<any>(null);
  const isEditDisabled = timeEndTest ? dayjs.utc(timeEndTest).isAfter(dayjs.utc()) : false;

  const listBreadcrumb = [
    {
      title: (
        <SafeLink to={`${routesConfig.courseManagement}/${routesConfig.courseList}`}>
          {t("Danh sách khoá học", "Danh sách khoá học")}
        </SafeLink>
      ),
    },
    {
      title: (
        <SafeLink
          to={`${routesConfig.courseManagement}/${courseId}/${routesConfig.gradingList}`}
        >
          {courseInfo?.name}
        </SafeLink>
      ),
    },
    {
      title: (
        <SafeLink
          to={`${routesConfig.courseManagement}/${courseId}/${routesConfig.grading}/${blockId}/${routesConfig.testClasses}`}
        >
          {unitName}
        </SafeLink>
      ),
    },

    { title: enrollClassName },

  ];

  const fetchStudentByEnrollClass = async () => {
    try {
      setIsLoading(true);
      const payload = {
        page: pageNumber,
        size: pageSize,
        keyword: searchValue,
        blockId: blockId,
        enrollClassId: classId,
      };
      const res = await getStudentByEnrollClass(payload);
      const data = res?.data?.data;
      if (data) {
        setEvaluationStudent(data.content || []);
        setTotalPage(data.total || 0);
        setUnitName(data.unitName)
        setEnrollClassName(data.enrollClassName)
        setTimeEndTest(data?.timeEnd)
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const getDetailCourseData = async () => {
      try {
        const res = await getCourseName(Number(courseId));
        setCourseInfo(res?.data?.data);
      } catch (error) {
        console.error(error);
      }
    };
    getDetailCourseData();
  }, [courseId]);

  useEffect(() => {
    fetchStudentByEnrollClass();
  }, [pageNumber, pageSize, reload]);

  const handleSubmitSearch = () => {
    setPageNumber(1);
    fetchStudentByEnrollClass();
  };


  const handleDownloadScoreList = async () => {
    try {
      const payload = {
        page: pageNumber,
        size: pageSize,
        keyword: searchValue,
        blockId: blockId,
        enrollClassId: classId,
      };
      setIsLoadingDownloadScoreList(true)
      await downloadScoreList(payload).then((res) => {
        const fileData = res.data;
        const blob = new Blob([fileData], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const downloadLink = document.createElement("a");
        downloadLink.href = URL.createObjectURL(blob);

        downloadLink.download = `Ketquahoctap_${courseId}_${enrollClassName}_${unitName}.xlsx`;

        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        message.success(t("Tải xuống thành công!", "Tải xuống thành công!"))
      })
    } catch (err) {
      console.error("Error downloading Excel file:", err);
    }
    finally {
      setIsLoadingDownloadScoreList(false)

    }
  }

  const handleDownloadFile = async () => {
    setIsLoadingDownloadScoreList(true)
    try {
      await downloadScoreTemplate(Number(classId), Number(blockId)).then((res) => {
        const fileData = res.data;

        const blob = new Blob([fileData], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const downloadLink = document.createElement("a");
        downloadLink.href = URL.createObjectURL(blob);

        downloadLink.download = `Ketquahoctap_${courseId}_${enrollClassName}_${unitName}.xlsx`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        message.success(t("Tải xuống thành công!", "Tải xuống thành công!"))
      })
    } catch (err) {
      console.error("Error downloading Excel file:", err);
    }
    finally {
      setIsLoadingDownloadScoreList(false)

    }
  }

  const handleImportExcel = async (formData: FormData) => {
    formData.append("blockId", blockId || "");
    formData.append("enrollClassId", classId || "");

    try {
      const res = await importScoreTemplate(formData);
      if (res.status === 200) {
        message.success(t("Thêm mới điểm thành công!", "Thêm mới điểm thành công!"));
        setReload(!reload);
      } else {
        message.error(t("Có lỗi xảy ra khi tải lên.", "Có lỗi xảy ra khi tải lên."));
      }
    } catch (err) {
      console.error(err);
      message.error(t("Lỗi khi tải lên, vui lòng thử lại.", "Lỗi khi tải lên, vui lòng thử lại."));
    }


  };

  const items: MenuProps["items"] = [
    {
      label: <div className="flex align-center gap-16">{t("Xuất bảng điểm", "Xuất bảng điểm")} {isLoadingDownloadScoreList ? <Spin indicator={<LoadingOutlined spin />} size="small" /> : null}</div>,
      key: "1",
      icon: <ExportOutlined />,
      onClick: handleDownloadScoreList,
    },
    {
      label: t("Import dữ liệu", "Import dữ liệu"),
      key: "2",
      icon: <ImportOutlined />,
      onClick: () => setIsExport(true),
      disabled: isEditDisabled,
    },
    {
      label: <div className="flex align-center gap-16">{t("Tải file mẫu", "Tải file mẫu")} {isLoadingDownloadScoreList ? <Spin indicator={<LoadingOutlined spin />} size="small" /> : null}</div>,
      key: "3",
      icon: <DownloadOutlined />,
      onClick: handleDownloadFile
    },
    {
      label: t("Lịch sử chỉnh sửa", "Lịch sử chỉnh sửa"),
      key: "4",
      icon: <HistoryOutlined />,
      onClick: () => setIsViewEditHistory(true)
    },
  ];

  const handleEditScore = async () => {
    if (!isEditingAll) {
      setIsEditingAll(true);
    } else {
      await handleSaveAll();
    }
  }

  const handleSaveAll = async () => {
    try {
      const values = await form.validateFields();
      const payload = evaluationStudent.map((item) => {
        const fieldName = `score_${item.id}`;
        const newScore = values[fieldName];
        return {
          userId: item.studentId,
          score: newScore != null ? Number(newScore) : null,
        };
      });

      const res = await updateStudentScore(Number(blockId), Number(classId), payload);
      if (res?.data?.success) {
        setReload(!reload)
        setIsEditingAll(false);
        message.success(t("Chỉnh sửa điểm thành công", "Chỉnh sửa điểm thành công"))
      } else {
        console.error("Cập nhật điểm lỗi:", res?.data?.message);
      }
    } catch (err) {
      console.error("Validate hoặc update lỗi:", err);
    }
  };

  const handleSaveRow = async (updatedStudent: EvaluationStudent) => {
    try {
      const res = await updateStudentScore(Number(blockId), Number(classId), [{
        userId: updatedStudent.studentId,
        score: updatedStudent.score || null
      }]);
      if (res?.data?.success) {
        setReload(!reload)
        message.success(t("Chỉnh sửa điểm thành công", "Chỉnh sửa điểm thành công"))

      } else {
        console.error("Cập nhật điểm không thành công:", res?.data?.message);
      }
    } catch (error) {
      console.error("Cập nhật điểm thất bại", error);
    }
  };

  const handleSendScoreResult = async () => {

    try {
      const payload = {
        enrollClassId: classId,
        blockId: blockId,
        courseId: courseId
      }

      await sendScoreResult(objectToQueryString(payload))
      message.success(t("Gửi kết quả chấm điểm thành công", "Gửi kết quả chấm điểm thành công"))
    } catch (error) {

    }
  }

  return (
    <div className="evaluation-students">
      <PageHeader
        title={enrollClassName}
        subTitle=""
        breadcrumb
        listBreadcrumb={listBreadcrumb}
        arrowLeft={imgPath + "arrowLeft.svg"}
        positionItem="column"
        headerGroupButton
        childrenGroupButton={
          <div className="button-container">
            <FormInput
              placeholder={t("Nhập tên học viên hoặc email", "Nhập tên học viên hoặc email")}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSubmitSearch()}
              afterPrefixIcon={<SearchOutlined onClick={handleSubmitSearch} />}
              style={{ minWidth: "260px" }}
            />
            <Button type="primary"

              onClick={() => {
                Modal.confirm({
                  title: `Xác nhận gửi kết quả`,
                  content: `Bạn có chắc chắn muốn gửi kết quả điểm tới sinh viên?`,
                  onOk: () => handleSendScoreResult(),
                  okText: t("Xác nhận", "Xác nhận"),
                  cancelText: t("Huỷ", "Huỷ"),
                  centered: true,
                  icon: <ExclamationCircleOutlined style={{ color: "#1677FF" }} />,
                  footer: (_, { OkBtn, CancelBtn }) => (
                    <>
                      <CancelBtn />
                      <OkBtn />
                    </>
                  ),
                })
              }}
              disabled={isEditDisabled}
            >{t("Gửi kết quả", "Gửi kết quả")}</Button>
            <Button
              onClick={handleEditScore}
              disabled={isEditDisabled}
            >{isEditingAll ? t("Lưu tất cả", "Lưu tất cả") : t("Chỉnh sửa", "Chỉnh sửa")}</Button>
            <Dropdown menu={{ items }}>
              <Button>
                <Space>
                  {t("Tuỳ chọn", "Tuỳ chọn")}
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
          </div>
        }
      />
      <div className="evaluation-students-content">
        <EditableTable
          form={form}
          data={evaluationStudent}
          isEditingAll={isEditingAll}
          onSaveRow={handleSaveRow}
          pageNumber={pageNumber}
          pageSize={pageSize}
          totalPage={totalPage}
          isLoading={isLoading}
          setPageNumber={setPageNumber}
          setPageSize={setPageSize}
          isEditDisabled={isEditDisabled}
        />
      </div>
      <ImportCommonModal
        open={isExport}
        handleCancel={() => setIsExport(false)}
        onImport={handleImportExcel}
        downloadTemplate={handleDownloadFile}
        title={t("Tải điểm từ file Excel", "Tải điểm từ file Excel")}
      />

      <HistoryEditScoreModal open={isViewEditHistory}
        handleCancel={() => setIsViewEditHistory(false)}
      />
    </div>
  );
};

export default EvaluationStudents;
